import React, { useEffect, useMemo, useState } from 'react';

import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';

import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import { Box, Button, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

import { ALLOWED_IMAGE_UPLOAD_ACCEPT } from '~/services/ImageService';

import DeleteConfirmationDialog from '../../DeleteConfirmationDialog';
import ImageEditor from '../ImageEditor';
import type { LocalImageEdit } from '../ImageEditor';
import type { MediaImage } from '../types';
import useImageDropUpload from '../useImageDropUpload';
import {
  getSyncedImagePublicIds,
  uploadTileContentTransitionSx,
  uploadTileDragActiveContentSx,
  uploadTileDragActiveSx,
  uploadTileTransitionSx,
} from '../utils';

import ImportImagesDialog from './ImportImagesDialog';
import OfferGalleryTile from './Tile';

interface Props {
  hiddenImagePublicIds: Array<string>;
  imageEdits: Record<string, LocalImageEdit>;
  imagePublicIdReplacements: Record<string, string>;
  imageOrderOverrides: Array<string>;
  savedImageOrder: Array<string>;
  isUploadDisabled?: boolean;
  mediaImages: Array<MediaImage>;
  selectedImagePublicIds: Array<string>;
  propertyId?: string;
  onDeleteImage: (publicId: string) => Promise<void> | void;
  onReorderImages: (orderedPublicIds: Array<string>) => Promise<void> | void;
  onSaveImageEdit: (publicId: string, edit: LocalImageEdit) => Promise<void> | void;
  onToggleHiddenImage: (publicId: string) => Promise<void>;
  onToggleSelected: (publicId: string) => void;
  onUploadImages: (files: Array<File>) => Promise<void> | void;
  onImportImages: (photoUnits: Array<{ id_cloudinary_external: string; title?: string }>) => Promise<number>;
}

export default function OfferGallery({
  hiddenImagePublicIds,
  imageEdits,
  imagePublicIdReplacements,
  imageOrderOverrides,
  savedImageOrder,
  isUploadDisabled,
  mediaImages,
  selectedImagePublicIds,
  propertyId,
  onDeleteImage,
  onReorderImages,
  onSaveImageEdit,
  onToggleHiddenImage,
  onToggleSelected,
  onUploadImages,
  onImportImages,
}: Props) {
  const galleryImages = useMemo(() => mediaImages.filter((image) => image.sourceType === 'offer'), [mediaImages]);
  const [orderedImagePublicIds, setOrderedImagePublicIds] = useState<Array<string>>(() =>
    galleryImages.map((image) => image.publicId),
  );
  const [imagePendingDeletion, setImagePendingDeletion] = useState<MediaImage | undefined>();
  const [imagePendingEdit, setImagePendingEdit] = useState<MediaImage | undefined>();
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const mediaImagesByPublicId = useMemo(
    () => new Map(galleryImages.map((image) => [image.publicId, image])),
    [galleryImages],
  );
  const orderedImages = orderedImagePublicIds
    .map((publicId) => mediaImagesByPublicId.get(publicId))
    .filter((image): image is MediaImage => !!image);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const { dragDropProps, isDragActive, uploadLabel } = useImageDropUpload(onUploadImages, isUploadDisabled);

  useEffect(() => {
    const baseOrder =
      imageOrderOverrides.length > 0
        ? imageOrderOverrides
        : savedImageOrder.length > 0
          ? savedImageOrder
          : galleryImages.map((image) => image.publicId);

    setOrderedImagePublicIds(getSyncedImagePublicIds(baseOrder, galleryImages, imagePublicIdReplacements));
  }, [galleryImages, imageOrderOverrides, imagePublicIdReplacements, savedImageOrder]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = orderedImagePublicIds.indexOf(String(active.id));
    const newIndex = orderedImagePublicIds.indexOf(String(over.id));

    if (oldIndex < 0 || newIndex < 0) {
      return;
    }

    const nextPublicIds = arrayMove(orderedImagePublicIds, oldIndex, newIndex);

    setOrderedImagePublicIds(nextPublicIds);
    void onReorderImages(nextPublicIds);
  };

  const handleCloseDeleteDialog = () => {
    setImagePendingDeletion(undefined);
  };

  const handleUploadInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);

    event.target.value = '';

    if (files.length > 0) {
      void onUploadImages(files);
    }
  };

  const handleConfirmDelete = async () => {
    if (!imagePendingDeletion) {
      return;
    }

    try {
      await onDeleteImage(imagePendingDeletion.publicId);
      setImagePendingEdit((currentImage) =>
        currentImage?.publicId === imagePendingDeletion.publicId ? undefined : currentImage,
      );
      setImagePendingDeletion(undefined);
    } catch {
      // Error already handled upstream.
    }
  };

  return (
    <>
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ px: 3, py: 2.5 }}>
          <Typography variant="h5">Offer gallery</Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button startIcon={<PhotoLibraryOutlinedIcon />} variant="text" onClick={() => setIsImportDialogOpen(true)}>
              Import images
            </Button>
            <Chip
              label={`${galleryImages.length} ${galleryImages.length === 1 ? 'image' : 'images'}`}
              color="info"
              variant="outlined"
            />
          </Stack>
        </Stack>
        <Divider />

        <DndContext onDragEnd={handleDragEnd} sensors={sensors} collisionDetection={closestCenter}>
          <SortableContext items={orderedImages.map((image) => image.publicId)} strategy={rectSortingStrategy}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                  xl: 'repeat(4, minmax(0, 1fr))',
                },
                gap: 2,
                p: 3,
              }}
            >
              {orderedImages.map((image, index) => (
                <OfferGalleryTile
                  key={image.publicId}
                  canToggleHidden={!!image.sourceImageId}
                  image={image}
                  isHidden={hiddenImagePublicIds.includes(image.publicId)}
                  isSelected={selectedImagePublicIds.includes(image.publicId)}
                  slotLabel={getMainImageSlotLabel(index)}
                  onDeleteImage={setImagePendingDeletion}
                  onEditImage={setImagePendingEdit}
                  onToggleHiddenImage={onToggleHiddenImage}
                  onToggleSelected={onToggleSelected}
                />
              ))}
              <Button
                component="label"
                variant="text"
                disabled={isUploadDisabled}
                {...dragDropProps}
                sx={{
                  ...mainImageUploadTileSx,
                  ...(isDragActive ? uploadTileDragActiveSx : undefined),
                }}
              >
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  spacing={0.75}
                  sx={{
                    width: '100%',
                    height: '100%',
                    color: 'primary.main',
                    ...uploadTileContentTransitionSx,
                    ...(isDragActive ? uploadTileDragActiveContentSx : undefined),
                  }}
                >
                  <AddPhotoAlternateOutlinedIcon fontSize="large" />
                  <Typography variant="body2" fontWeight={700} textAlign="center">
                    {uploadLabel}
                  </Typography>
                </Stack>
                <Chip label={getMainImageSlotLabel(orderedImages.length)} size="small" sx={mainImageSlotChipSx} />
                <input
                  hidden
                  accept={ALLOWED_IMAGE_UPLOAD_ACCEPT}
                  multiple
                  type="file"
                  onChange={handleUploadInputChange}
                />
              </Button>
            </Box>
          </SortableContext>
        </DndContext>
      </Paper>

      <DeleteConfirmationDialog
        body="This will remove the image from the offer gallery. This action cannot be undone."
        open={!!imagePendingDeletion}
        title="Delete image?"
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
      />

      <ImageEditor
        edit={imagePendingEdit ? imageEdits[imagePendingEdit.publicId] : undefined}
        image={imagePendingEdit}
        open={!!imagePendingEdit}
        onClose={() => setImagePendingEdit(undefined)}
        onDeleteImage={(image) => {
          setImagePendingDeletion(image);
        }}
        onSave={onSaveImageEdit}
      />

      <ImportImagesDialog
        existingImageIds={mediaImages.map((image) => image.publicId)}
        propertyId={propertyId}
        open={isImportDialogOpen}
        onClose={() => setIsImportDialogOpen(false)}
        onImport={onImportImages}
      />
    </>
  );
}

function getMainImageSlotLabel(index: number) {
  return index === 0 ? '#1 Hero' : `#${index + 1}`;
}

const mainImageUploadTileSx: SystemStyleObject<Theme> = {
  position: 'relative',
  overflow: 'hidden',
  aspectRatio: '16 / 9',
  width: '100%',
  border: 1,
  borderStyle: 'dashed',
  borderColor: 'primary.main',
  borderRadius: 1,
  bgcolor: 'action.hover',
  minWidth: 0,
  p: 0,
  textTransform: 'none',
  ...uploadTileTransitionSx,
  '&:hover': {
    bgcolor: 'action.selected',
  },
};

const mainImageSlotChipSx: SystemStyleObject<Theme> = {
  position: 'absolute',
  top: 10,
  left: 10,
  bgcolor: 'grey.800',
  color: 'common.white',
  fontWeight: 700,
};
